import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { MemberCancelReasonClient, MemberCancelReasonDTO } from '../services/apiClient';
import { API_BASE_URL } from '../config/config';
import axiosInstance from '../services/axiosInstance';

const apiClient = new MemberCancelReasonClient(API_BASE_URL, axiosInstance);
const MAX_DROPDOWN_PAGE_SIZE = 9999;

// Cached hook for dropdowns - get all active member cancel reasons
export const useMemberCancelReasons = () => {
  return useQuery({
    queryKey: ['memberCancelReasons', 'all'],
    queryFn: async () => {
      const response = await apiClient.searchMemberCancelReasons({
        page: 1,
        pageSize: MAX_DROPDOWN_PAGE_SIZE,
        sortBy: 'DisplayPosition',
        sortDescending: false,
      });
      return response?.data?.results || [];
    },
    staleTime: 4 * 60 * 60 * 1000, // 4 hours
    refetchOnReconnect: true,
  });
};

// Non-cached search hook for list pages
export const useMemberCancelReasonSearch = () => {
  const search = async (params: any) => {
    const response = await apiClient.searchMemberCancelReasons(params);
    return {
      results: response?.data?.results || [],
      totalCount: response?.data?.totalCount || 0,
    };
  };

  return { search };
};

// Get by ID
export const useMemberCancelReasonById = (id: string | undefined) => {
  return useQuery({
    queryKey: ['memberCancelReasons', id],
    queryFn: async () => {
      if (!id) return null;
      const response = await apiClient.getMemberCancelReasonById(id);
      return response?.data || null;
    },
    enabled: !!id,
  });
};

// Create mutation
export const useCreateMemberCancelReason = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (data: MemberCancelReasonDTO) => {
      return await apiClient.createMemberCancelReason(data);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['memberCancelReasons'] });
    },
  });
};

// Update mutation
export const useUpdateMemberCancelReason = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ id, data }: { id: string; data: MemberCancelReasonDTO }) => {
      return await apiClient.updateMemberCancelReason(id, data);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['memberCancelReasons'] });
    },
  });
};

// Delete mutation
export const useDeleteMemberCancelReason = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (id: string) => {
      return await apiClient.deleteMemberCancelReason(id);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['memberCancelReasons'] });
    },
  });
};